import { TFunction } from "i18next";
import { Device, MonitoringSource } from "@services/devices/types";
import { hasLicense } from "@common/utils/user";
import { displayDateTime } from "@common/utils/localTime.utils";
import { RealTimeMonitoringProps } from "@common/components/ListDetailViews/common/RealTimeStatus/type";
import { TroubleshootClickHandler } from "./types";
import { generateGetErrorMessageFunction } from "./helpers/generateGetErrorMessageFunction";
import {
  hasRoomController,
  has3rdPartyManagement,
  hasDMCA,
  hasCommProxy,
} from "./helpers/checkMonitoringSource";
import { getAggregatorControllerStatus } from "./helpers/getAggregatorControllerStatus";
import { getDeviceStatusByDMCA } from "./helpers/getDeviceStatusByDMCA";
import { getDeviceStatusByAggregatorController } from "./helpers/getDeviceStatusByAggregatorController";
import { getDeviceLastUpdated } from "../getDeviceLastUpdated";
import { getDeviceStatusByDirectSource } from "./helpers/getDeviceStatusByDirectSource";

type GetDeviceMonitoringStatusConfig = {
  device: Device | undefined;
  t: TFunction;
  onTroubleshootClick?: TroubleshootClickHandler;
  isDeviceCard?: boolean;
  tooltipAsText?: boolean;
};

export const getDeviceMonitoringSourcesStatus = ({
  device,
  t,
  onTroubleshootClick,
  isDeviceCard = false,
  tooltipAsText = false,
}: GetDeviceMonitoringStatusConfig) => {
  const canAITroubleshoot = hasLicense(
    device?.room?.location?.account,
    "aiTroubleshooting"
  );

  const getErrorMessage = generateGetErrorMessageFunction(t, device);

  const deviceMonitoringSourceStatuses: RealTimeMonitoringProps[] = [];
  if (device) {
    const isMultipleMonitoring = (device?.monitoringSources?.length ?? 0) > 1;
    const isDMCA = hasDMCA(device);
    const is3rdPartyManagement = has3rdPartyManagement(device);
    const isRoomController = hasRoomController(device);
    const isCommProxy = hasCommProxy(device);

    const lastUpdatedValue = getDeviceLastUpdated(device);
    const lastUpdatedLabel = lastUpdatedValue
      ? t("Last updated : {{ DATE }}", {
          DATE: displayDateTime(lastUpdatedValue),
        })
      : "";

    const deviceStatusByDirectSource = getDeviceStatusByDirectSource({
      device,
      lastUpdatedLabel,
      canAITroubleshoot,
      isDeviceCard,
      onTroubleshootClick,
      getErrorMessage,
      t,
      tooltipAsText,
    });

    const {
      deviceStatusByAggregatorController,
      deviceStatusByAggregatorControllerLabel,
    } = getDeviceStatusByAggregatorController({
      device,
      canAITroubleshoot,
      lastUpdatedLabel,
      isDeviceCard,
      monitoringSource: is3rdPartyManagement
        ? MonitoringSource._3rdPartyManagement
        : MonitoringSource.RoomController,
      getErrorMessage,
      onTroubleshootClick,
      t,
      tooltipAsText,
    });

    const deviceStatusByDMCA = getDeviceStatusByDMCA({
      device,
      lastUpdatedLabel,
      canAITroubleshoot,
      isDeviceCard,
      getErrorMessage,
      onTroubleshootClick,
      t,
      tooltipAsText,
    });

    if (isMultipleMonitoring) {
      if (isCommProxy) {
        deviceMonitoringSourceStatuses.push({
          label: device.name,
          itemsMonitoring: deviceStatusByDirectSource,
        });
      }

      if (is3rdPartyManagement || isRoomController) {
        deviceMonitoringSourceStatuses.push({
          label: deviceStatusByAggregatorControllerLabel,
          itemsMonitoring: [deviceStatusByAggregatorController],
        });
      }

      if (isDMCA) {
        deviceMonitoringSourceStatuses.push({
          label: "DMCA",
          itemsMonitoring: [deviceStatusByDMCA],
        });
      }
    } else {
      if (is3rdPartyManagement || isRoomController) {
        if (!isDeviceCard) {
          const aggregatorControllerStatus = getAggregatorControllerStatus({
            device,
            canAITroubleshoot,
            isDeviceCard,
            lastUpdatedLabel,
            monitoringSource: is3rdPartyManagement
              ? MonitoringSource._3rdPartyManagement
              : MonitoringSource.RoomController,
            getErrorMessage,
            onTroubleshootClick,
            t,
            tooltipAsText,
          });

          deviceMonitoringSourceStatuses.push({
            label: device?.aggregator?.name,
            itemsMonitoring: aggregatorControllerStatus,
          });
        }

        deviceMonitoringSourceStatuses.push({
          label: device?.name,
          itemsMonitoring: [deviceStatusByAggregatorController],
        });
      } else if (isCommProxy) {
        deviceMonitoringSourceStatuses.push({
          itemsMonitoring: deviceStatusByDirectSource,
        });
      } else if (isDMCA) {
        deviceMonitoringSourceStatuses.push({
          itemsMonitoring: [deviceStatusByDMCA],
        });
      }
    }
  }

  return deviceMonitoringSourceStatuses;
};
